import { Autocomplete } from '@material-ui/lab'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import Swal from 'sweetalert2'
import { useSuggestAddress } from '../../../../../hooks/suggest/use-suggest-address'
import { filterFilledParams } from '../../../../../hooks/use-search-params'
import { normalizeString } from '../../../../../utils/autocomplete'
import usePatchAddressData from '../../../hooks/use-address-patch-data'
import { useEditAddressStore } from '../../../stores/edit-address-store'
import { validateForm } from '../../../utils'
import {
  AddressFormActionStyled,
  AddressFormStyled,
  AutocompleteStyled,
} from '../account-address-styled'
import { ButtonPrimary, ButtonSecondaryOutlined } from '../../../../_ui/button'
import { InputStyled } from '../../../../_ui/input'

const EditAddressForm = () => {
  const queryClient = useQueryClient()

  // Zustand store
  const form = useEditAddressStore(s => s.form)
  const setForm = useEditAddressStore(s => s.setForm)
  const closeModal = useEditAddressStore(s => s.closeModal)
  const clearOnCloseComplete = useEditAddressStore(s => s.clearOnCloseComplete)
  const onCloseComplete = useEditAddressStore(s => s.onCloseComplete)

  const handleOnSuccessUpdate = useCallback((data) => {
    queryClient.invalidateQueries(['useMemberAddressData'])
    closeModal()
    onCloseComplete?.(data)
    clearOnCloseComplete?.()
  }, [queryClient, closeModal, onCloseComplete, clearOnCloseComplete])

  const { mutateAsync: patchData, isLoading: isLoadingPatchAddress } =
    usePatchAddressData({
      onSuccess: handleOnSuccessUpdate,
    })

  const handleEditAddress = useCallback(() => {
    const {
      province_id,
      city_id,
      subdistrict_id,
      rajaOngkirId,
      provinceName,
      cityName,
      districtName,
      subdistrictName,
      ...rest
    } = form || {}

    const checkForm = {
      rajaOngkirId,
      provinceName,
      cityName,
      districtName,
      subdistrictName,
      ...rest,
    }

    const isValid = validateForm(checkForm)
    if (!isValid) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Data',
        text: 'Silakan lengkapi semua kolom yang dibutuhkan.',
      })
      return
    }

    const payload = {
      raja_ongkir_id: rajaOngkirId,
      province_id,
      city_id,
      subdistrict_id,
      province_name: provinceName,
      city_name: cityName,
      district_name: districtName,
      subdistrict_name: subdistrictName,
      ...rest,
    }

    return patchData(payload)
  }, [form, patchData])

  const queryParamsSuggest = useMemo(() => {
    const { cityName, districtName, subdistrictName } = form || {}

    const isLocationFilled = cityName || districtName || subdistrictName

    if (isLocationFilled) {
      return filterFilledParams({
        name: [subdistrictName, districtName, cityName]
          .filter(Boolean)
          .join(' '),
      })
    }

    return null
  }, [form])

  const {
    setSuggestKeyword,
    suggestAddress = [],
    isLoading: isSuggestLoading,
  } = useSuggestAddress(queryParamsSuggest)

  const disabled = isSuggestLoading || isLoadingPatchAddress

  const handleChange = useCallback(
    e => {
      const { name, value } = e.target
      setForm(prev => ({ ...prev, [name]: value }))
    },
    [setForm]
  )

  const handleChangeKeyword = useCallback(
    e => {
      setSuggestKeyword(e.target.value)
    },
    [setSuggestKeyword]
  )

  const handleChangeAddress = useCallback(
    (e, v) => {
      if (v) {
        setForm(prev => {
          return {
            ...prev,
            city_id: v.cityId,
            postal_code: v.codePost,
            subdistrict_id: v.subdistrictId,
            province_id: v.provinceId,
            rajaOngkirId: v.rajaOngkirId,
            provinceName: v.provinceName,
            cityName: v.cityName,
            districtName: v.districtName,
            subdistrictName: v.subdistrictName,
          }
        })
      } else {
        setForm(prev => ({
          ...prev,
          city_id: null,
          postal_code: null,
          subdistrict_id: null,
          province_id: null,
          rajaOngkirId: null,
          provinceName: null,
          cityName: null,
          districtName: null,
          subdistrictName: null,
        }))
      }
    },
    [setForm]
  )

  const autoCompleteValue = useMemo(() => {
    if (!suggestAddress || !form?.rajaOngkirId) return null
    return (
      suggestAddress.find(
        item => item?.rajaOngkirId?.toString() === form.rajaOngkirId?.toString()
      ) || null
    )
  }, [suggestAddress, form?.rajaOngkirId])

  return (
    <AddressFormStyled>
      <InputStyled
        autoFocus
        required
        fullWidth
        name='name'
        label='Nama'
        variant='outlined'
        size='small'
        InputLabelProps={{ shrink: true }}
        onChange={handleChange}
        value={form?.name || ''}
      />
      <InputStyled
        required
        fullWidth
        name='phone'
        label='No. Ponsel'
        variant='outlined'
        size='small'
        type='number'
        InputLabelProps={{ shrink: true }}
        onChange={handleChange}
        value={form?.phone || ''}
      />
      <AutocompleteStyled>
        <Autocomplete
          freeSolo
          id='address-suggest'
          disabled={disabled}
          options={suggestAddress || []}
          getOptionLabel={option => option?.name || ''}
          filterOptions={(options, state) => {
            const input = normalizeString(state.inputValue)
            return options.filter(option =>
              normalizeString(option.name).includes(input)
            )
          }}
          value={autoCompleteValue}
          onChange={handleChangeAddress}
          loading={isSuggestLoading}
          renderInput={params => (
            <InputStyled
              required
              {...params}
              fullWidth
              InputLabelProps={{ shrink: true }}
              variant='outlined'
              label='Kota atau Kecamatan'
              placeholder='Tulis kota/kecamatan'
              size='small'
              onChange={handleChangeKeyword}
              inputProps={{
                ...params.inputProps,
                autoComplete: 'new-password',
              }}
            />
          )}
        />
      </AutocompleteStyled>
      <InputStyled
        fullWidth
        required
        className='postal_code'
        name='postal_code'
        label='Kode Pos'
        variant='outlined'
        size='small'
        type='number'
        InputLabelProps={{ shrink: true }}
        onChange={handleChange}
        value={form?.postal_code || ''}
      />
      <InputStyled
        required
        multiline
        maxRows={4}
        fullWidth
        name='address'
        label='Alamat'
        variant='outlined'
        size='small'
        type='text'
        placeholder='Tulis nama jalan, nomor rumah, nomor kompleks, nama gedung'
        InputLabelProps={{ shrink: true }}
        onChange={handleChange}
        value={form?.address || ''}
      />
      <AddressFormActionStyled>
        <ButtonSecondaryOutlined
          onClick={closeModal}
          disabled={disabled}
          variant='outlined'
          size='small'
        >
          Batal
        </ButtonSecondaryOutlined>
        <ButtonPrimary
          onClick={handleEditAddress}
          disabled={disabled}
          variant='outlined'
          size='small'
        >
          Simpan
        </ButtonPrimary>
      </AddressFormActionStyled>
    </AddressFormStyled>
  )
}

export default EditAddressForm
